import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import fileDownload from 'js-file-download';
import kebabCase from 'lodash.kebabcase';
import isEmpty from 'lodash.isempty';
import m from 'moment';

import { metricsApi } from '../../api';
import { RESOLUTION_24H, RESOLUTION_1H, RESOLUTION_10MIN, TIMESTAMP_FORMAT_HUMANIZED } from '../../constants/app';
import * as p from '../../constants/params';

import * as routerSelectors from '../router/selectors';
import { getMetricConfig } from '../utils/selectors';
import { pushNotification } from '../ui/actions';

import * as actions from './actions';
import * as selectors from './selectors';
import { formatForDownload } from './utils';

function* retrieveParams() {
  // required
  const asset = yield select(selectors.selectComputedAsset);
  const metricCode = yield select(selectors.selectComputedMetricCode);
  // optional
  const currency = yield select(selectors.selectComputedCurrency);
  const exchange = yield select(selectors.selectComputedExchange);
  const miner = yield select(selectors.selectComputedMiner);
  const resolution = yield select(selectors.selectValidatedResolution);

  const params = {
    [p.ASSET]: asset,
    [p.CURRENCY]: currency,
    [p.EXCHANGE]: exchange,
    [p.METRIC]: metricCode,
    [p.MINER]: miner,
    [p.RESOLUTION]: resolution,
  };

  return {
    asset,
    currency,
    exchange,
    metricCode,
    miner,
    params,
    resolution,
  };
}

function* retrieveParamsFromUrl() {
  // required
  const asset = yield select(routerSelectors.selectAsset);
  const metricCode = yield select(routerSelectors.selectMetricCode);
  // optional
  const currency = yield select(routerSelectors.selectCurrency);
  const date = yield select(routerSelectors.selectDate);
  const exchange = yield select(routerSelectors.selectExchange);
  const miner = yield select(routerSelectors.selectMiner);
  const movingAverage = yield select(routerSelectors.selectMovingAverage);
  const resolution = yield select(routerSelectors.selectResolution);
  const since = yield select(routerSelectors.selectSince);
  const until = yield select(routerSelectors.selectUntil);

  return {
    asset,
    date,
    currency,
    exchange,
    metricCode,
    miner,
    movingAverage,
    resolution,
    since,
    until,
  };
}

function* getMetricData({ metricCode, path, ...rest }) {
  try {
    yield put({
      type: actions.GET_METRIC_PENDING,
      payload: {
        metricCode,
      },
    });

    const data = yield call(metricsApi.getMetric, path, rest);

    yield put({ type: actions.GET_METRIC_FULFILLED, payload: data });
  } catch (e) {
    yield put({ type: actions.GET_METRIC_REJECTED, payload: e });
  }
}

function* getMetricDependencyData({ metricCode, path, dependency }) {
  try {
    yield put({
      type: actions.GET_DEPENDENCY_PENDING,
      payload: {
        metricCode,
      },
    });

    const data = yield call(metricsApi.getMetricDependency, path, dependency);

    yield put({ type: actions.GET_DEPENDENCY_FULFILLED, payload: data });
  } catch (e) {
    yield put({ type: actions.GET_DEPENDENCY_REJECTED, payload: e });
  }
}

function* getPriceData(payload) {
  try {
    yield put({ type: actions.GET_PRICES_PENDING });

    const data = yield call(metricsApi.getPrices, payload);

    yield put({ type: actions.GET_PRICES_FULFILLED, payload: data });
  } catch (e) {
    yield put({ type: actions.GET_PRICES_REJECTED, payload: e });
  }
}

function* getDownloadMetricData({ path, format, ...rest }) {
  try {
    yield put({ type: actions.DOWNLOAD_METRIC_DATA_PENDING, format });

    const data = yield call(metricsApi.getMetric, path, { format, ...rest });

    yield put({ type: actions.DOWNLOAD_METRIC_DATA_FULFILLED, format });

    return data;
  } catch (e) {
    yield put({ type: actions.DOWNLOAD_METRIC_DATA_REJECTED, format });
    yield put(
      pushNotification({
        title: 'Error downloading data',
        description: e.message,
        duration: 6,
        type: 'error',
      }),
    );

    return null;
  }
}

function* generateParams(vals = []) {
  const resolution = yield select(selectors.selectValidatedResolution);
  let since = yield select(selectors.selectMetricStart24h);

  if (resolution === RESOLUTION_1H) {
    since = yield select(selectors.selectMetricStart1h);
  }

  if (resolution === RESOLUTION_10MIN) {
    since = yield select(selectors.selectMetricStart10min);
  }

  const c = yield select(selectors.selectComputedCurrency);
  const currency = vals.includes(p.CURRENCY) ? c : undefined;

  const e = yield select(selectors.selectComputedExchange);
  const exchange = vals.includes(p.EXCHANGE) ? e : undefined;

  const mi = yield select(selectors.selectComputedMiner);
  const miner = vals.includes(p.MINER) ? mi : undefined;

  return { currency, exchange, miner, since };
}

function* changeResolution({ payload }) {
  const { resolution } = payload;

  const isMetricLocked = yield select(selectors.selectIsMetricLocked);
  const isMetricDisabled = yield select(selectors.selectIsMetricDisabled);

  if (isMetricLocked || isMetricDisabled) {
    return;
  }

  const asset = yield select(selectors.selectComputedAsset);
  const metricCode = yield select(selectors.selectComputedMetricCode);
  const { path, price, requiredParams, dependency } = getMetricConfig(metricCode);
  const { currency, exchange, miner, since } = yield call(generateParams, requiredParams);

  const mArgs = {
    asset,
    currency,
    exchange,
    metricCode,
    miner,
    path,
    resolution,
    since,
  };

  const calls = [call(getMetricData, mArgs)];

  if (price) {
    const pArgs = {
      asset,
      resolution,
      since,
    };

    calls.push(call(getPriceData, pArgs));
  }

  if (dependency) {
    calls.push(call(getMetricDependencyData, { metricCode, path, dependency }));
  }

  yield all(calls);
}

function* changeResolutionToLow() {
  yield call(changeResolution, { payload: { resolution: RESOLUTION_24H } });
}

function* getMetricAndPrice({ payload }) {
  const { metricCode } = payload;

  const isMetricLocked = yield select(selectors.selectIsMetricLocked);
  const isMetricDisabled = yield select(selectors.selectIsMetricDisabled);

  if (isMetricLocked || isMetricDisabled) {
    return;
  }

  const isResolutionValid = yield select(selectors.selectIsResolutionValid);
  if (!isResolutionValid) {
    yield call(changeResolutionToLow);

    return;
  }

  const asset = yield select(selectors.selectComputedAsset);
  const resolution = yield select(selectors.selectComputedResolution);
  const { path, price, requiredParams, dependency } = getMetricConfig(metricCode);
  const { currency, exchange, miner, since } = yield call(generateParams, requiredParams);

  const mArgs = {
    asset,
    currency,
    exchange,
    metricCode,
    miner,
    path,
    resolution,
    since,
  };

  const calls = [call(getMetricData, mArgs)];

  if (price) {
    const pArgs = {
      asset,
      resolution,
      since,
    };

    calls.push(call(getPriceData, pArgs));
  }

  if (dependency) {
    calls.push(call(getMetricDependencyData, { metricCode, path, dependency }));
  }

  yield all(calls);
}

function* changeMetric({ payload }) {
  const { metricCode } = payload;

  const isMetricLocked = yield select(selectors.selectIsMetricLocked);
  if (isMetricLocked) {
    return;
  }

  const isResolutionValid = yield select(selectors.selectIsResolutionValid);
  if (!isResolutionValid) {
    yield call(changeResolutionToLow);

    return;
  }

  const asset = yield select(selectors.selectComputedAsset);
  const resolution = yield select(selectors.selectComputedResolution);
  const { path, requiredParams, price, dependency } = getMetricConfig(metricCode);
  const { currency, exchange, miner, since } = yield call(generateParams, requiredParams);

  const mArgs = {
    asset,
    currency,
    exchange,
    metricCode,
    miner,
    path,
    resolution,
    since,
  };

  const calls = [call(getMetricData, mArgs)];

  if (price) {
    const pArgs = {
      asset,
      resolution,
      since,
    };

    calls.push(call(getPriceData, pArgs));
  }

  if (dependency) {
    calls.push(call(getMetricDependencyData, { metricCode, path, dependency }));
  }

  yield all(calls);
}

function* changeCurrency({ payload }) {
  const { currency } = payload;

  const isMetricLocked = yield select(selectors.selectIsMetricLocked);
  const isMetricDisabled = yield select(selectors.selectIsMetricDisabled);

  if (isMetricLocked || isMetricDisabled) {
    return;
  }

  const isResolutionValid = yield select(selectors.selectIsResolutionValid);
  if (!isResolutionValid) {
    yield call(changeResolutionToLow);

    return;
  }

  const asset = yield select(selectors.selectComputedAsset);
  const resolution = yield select(selectors.selectComputedResolution);
  const metricCode = yield select(selectors.selectComputedMetricCode);
  const { path, requiredParams } = getMetricConfig(metricCode);
  const { exchange, miner, since } = yield call(generateParams, requiredParams);

  const mArgs = {
    asset,
    currency,
    exchange,
    metricCode,
    miner,
    path,
    resolution,
    since,
  };

  yield call(getMetricData, mArgs);
}

function* changeAsset({ payload }) {
  const { asset } = payload;

  const isMetricLocked = yield select(selectors.selectIsMetricLocked);
  const isMetricDisabled = yield select(selectors.selectIsMetricDisabled);

  if (isMetricLocked || isMetricDisabled) {
    return;
  }

  const isResolutionValid = yield select(selectors.selectIsResolutionValid);
  if (!isResolutionValid) {
    yield call(changeResolutionToLow);

    return;
  }

  const resolution = yield select(selectors.selectComputedResolution);
  const metricCode = yield select(selectors.selectComputedMetricCode);
  const { path, requiredParams } = getMetricConfig(metricCode);
  const { currency, exchange, since } = yield call(generateParams, requiredParams);

  const mArgs = {
    asset,
    currency,
    exchange,
    metricCode,
    path,
    resolution,
    since,
  };

  const pArgs = {
    asset,
    resolution,
    since,
  };

  yield all([call(getMetricData, mArgs), call(getPriceData, pArgs)]);
}

function* changeExchange({ payload }) {
  const { exchange } = payload;

  const isMetricLocked = yield select(selectors.selectIsMetricLocked);
  const isMetricDisabled = yield select(selectors.selectIsMetricDisabled);

  if (isMetricLocked || isMetricDisabled) {
    return;
  }

  const isResolutionValid = yield select(selectors.selectIsResolutionValid);
  if (!isResolutionValid) {
    yield call(changeResolutionToLow);

    return;
  }

  const asset = yield select(selectors.selectComputedAsset);
  const resolution = yield select(selectors.selectComputedResolution);
  const metricCode = yield select(selectors.selectComputedMetricCode);
  const { path, requiredParams } = getMetricConfig(metricCode);
  const { currency, since } = yield call(generateParams, requiredParams);

  const mArgs = {
    asset,
    currency,
    exchange,
    metricCode,
    path,
    resolution,
    since,
  };

  yield call(getMetricData, mArgs);
}

function* changeMiner({ payload }) {
  const { miner } = payload;

  const isMetricLocked = yield select(selectors.selectIsMetricLocked);
  const isMetricDisabled = yield select(selectors.selectIsMetricDisabled);

  if (isMetricLocked || isMetricDisabled) {
    return;
  }

  const isResolutionValid = yield select(selectors.selectIsResolutionValid);
  if (!isResolutionValid) {
    yield call(changeResolutionToLow);

    return;
  }

  const asset = yield select(selectors.selectComputedAsset);
  const resolution = yield select(selectors.selectComputedResolution);
  const metricCode = yield select(selectors.selectComputedMetricCode);
  const { path, requiredParams } = getMetricConfig(metricCode);
  const { currency, since } = yield call(generateParams, requiredParams);

  const mArgs = {
    asset,
    currency,
    miner,
    metricCode,
    path,
    resolution,
    since,
  };

  yield call(getMetricData, mArgs);
}

function* downloadTimeSeriesData({ payload }) {
  const { format } = payload;

  // get current metric
  const { path, shortName } = yield select(selectors.selectMetricConfig);
  const fileName = `${kebabCase(shortName)}.${format}`;

  // get params
  const { asset, currency, exchange, resolution } = yield call(retrieveParams);
  const { since, until } = yield call(retrieveParamsFromUrl);

  const data = yield getDownloadMetricData({
    asset,
    currency,
    exchange,
    format,
    path,
    resolution,
    since,
    timestampFormat: TIMESTAMP_FORMAT_HUMANIZED,
    until,
  });

  if (isEmpty(data)) {
    return;
  }

  const formattedData = formatForDownload(data);
  fileDownload(formattedData, fileName);
}

function* downloadBarPlotData({ payload }) {
  const { format } = payload;

  // get current metric
  const { path, shortName } = yield select(selectors.selectMetricConfig);
  const fileName = `${kebabCase(shortName)}.${format}`;

  // get params
  const { asset, currency, exchange, resolution } = yield call(retrieveParams);
  const { date } = yield call(retrieveParamsFromUrl);

  const since = date || m().unix();
  const until = m.unix(since).add(1, 'day').unix();

  const data = yield getDownloadMetricData({
    asset,
    currency,
    exchange,
    format,
    path,
    resolution,
    since,
    timestampFormat: TIMESTAMP_FORMAT_HUMANIZED,
    until,
  });

  if (isEmpty(data)) {
    return;
  }

  const formattedData = formatForDownload(data);
  fileDownload(formattedData, fileName);
}

export function* watchMetricsGetMetric() {
  yield takeLatest(actions.GET_METRIC_AND_PRICE, getMetricAndPrice);
}

export function* watchMetricsChangeAsset() {
  yield takeLatest(actions.CHANGE_ASSET, changeAsset);
}

export function* watchMetricsChangeMetric() {
  yield takeLatest(actions.CHANGE_METRIC, changeMetric);
}

export function* watchMetricsChangeResolution() {
  yield takeLatest(actions.CHANGE_RESOLUTION, changeResolution);
}

export function* watchMetricsChangeCurrency() {
  yield takeLatest(actions.CHANGE_CURRENCY, changeCurrency);
}

export function* watchMetricsChangeExchange() {
  yield takeLatest(actions.CHANGE_EXCHANGE, changeExchange);
}

export function* watchMetricsChangeMiner() {
  yield takeLatest(actions.CHANGE_MINER, changeMiner);
}

export function* watchDownloadTimeSeriesData() {
  yield takeLatest(actions.DOWNLOAD_TIME_SERIES_DATA, downloadTimeSeriesData);
}

export function* watchDownloadBarPlotData() {
  yield takeLatest(actions.DOWNLOAD_BAR_PLOT_DATA, downloadBarPlotData);
}
